import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import GSDevTools from 'gsap/dist/GSDevTools'
import SplitText from 'gsap/dist/SplitText'
import parse from 'html-react-parser'
import { useLenis } from 'lenis/react'

import Container from '@/components/base/container'
import MagneticVideo from '@/components/base/magnetic-video'
import Media from '@/components/base/media'
import ScrollCta from '@/components/base/scroll-cta'
import Section from '@/components/base/section'
import {
  linesRevealAnimation,
  type SharedAnimation
} from '@/components/sections/animations/shared'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { useHomeHeroImageLoader } from '@/hooks/use-home-hero-image-loader'
import { useHomeHeroStore } from '@/store/home-hero'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPFile, WPImage, WPMedia, WPPageData } from '@/types'

type HomeHeroProps = {
  pageProps: WPPageData
}

type HeroData = {
  intro?: 'images' | 'video' | 'none'
  intro_video?: WPFile
  intro_images?: {
    title_1?: string
    title_2?: string
    title_3?: string
    image_1?: WPImage
    image_2?: WPImage
    image_3?: WPImage
  }
  media?: WPMedia
  title?: string
  text?: string
  video?:
    | {
        video_preview?: WPFile
        video?: WPFile
      }
    | WPFile
}

export function splitLinesReveal(element: any) {
  const split = new SplitText(element, {
    type: 'lines',
    mask: 'lines',
    autoSplit: true,
    linesClass: 'gsap-line-wrap'
  })

  split.lines.forEach((line) => {
    const inner = document.createElement('div')
    inner.className = 'line-inner'

    while (line.firstChild) {
      inner.appendChild(line.firstChild)
    }

    line.appendChild(inner)
  })

  return {
    split,
    revert: () => split.revert()
  }
}

type HomeHeroDebugWindow = Window & {
  __MODULNOVA_GSAP__?: {
    homeHeroIntro?: gsap.core.Timeline
    homeHeroDevTools?: {
      kill: () => void
    }
  }
}

type HomeHeroDevTools = {
  kill: () => void
}

const GSAP_DEBUG = false // process.env.NODE_ENV === 'development'

const safelyKillDevTools = (devTools?: HomeHeroDevTools) => {
  if (!devTools) {
    return
  }

  try {
    devTools.kill()
  } catch (error) {
    if (
      !(error instanceof TypeError) ||
      !error.message.includes('Cannot read properties of null')
    ) {
      throw error
    }
  }
}

const HomeHero = ({ pageProps }: HomeHeroProps) => {
  const hero = (pageProps.acf as { hero?: HeroData } | undefined)?.hero
  const { intro, intro_images, intro_video, media, title, text, video } =
    hero ?? {}

  const rootRef = useRef<HTMLDivElement>(null)

  const lenis = useLenis()
  const homeIntroPlayed = useHomeHeroStore((state) => state.homeIntroPlayed)
  const markIntroPlayed = useHomeHeroStore((state) => state.markIntroPlayed)
  const siteLoaded = useLoaderStore((state) => state.siteLoaded)
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)

  const { strings } = useStore()

  const setHomeIntroRunning = useHomeHeroStore(
    (state) => state.setHomeIntroRunning
  )

  const isFirstVisit = useRef(!homeIntroPlayed)
  const skipTimelineRef = useRef<gsap.core.Timeline | null>(null)
  const videoIntroHandledRef = useRef(false)
  const introCompletedRef = useRef(false)
  const [videoIntroDismissed, setVideoIntroDismissed] = useState(false)

  const introMode =
    intro === 'video' && !intro_video?.url ? 'none' : (intro ?? 'images')
  const isIntroImages = introMode === 'images'
  const isIntroVideo = introMode === 'video'
  const isIntroNone = introMode === 'none'

  const shouldPlayFullIntro = isIntroImages && isFirstVisit.current
  const shouldPlayVideoIntro = isIntroVideo && isFirstVisit.current
  const showVideoIntro = shouldPlayVideoIntro && !videoIntroDismissed
  const showSkipIntro = showVideoIntro

  const introVideoMedia = useMemo(() => {
    if (!intro_video?.url) return null

    return {
      video: {
        url: intro_video.url,
        id: intro_video.id,
        title: intro_video.title,
        caption: intro_video.caption,
        description: intro_video.description,
        filesize: intro_video.filesize,
        mimeType: intro_video.mime_type || 'video/mp4'
      }
    } as WPMedia
  }, [intro_video])

  const heroMagneticVideo = useMemo(() => {
    if (!video || !('video_preview' in video)) {
      return {
        videoPreview: undefined,
        videoFile: undefined
      }
    }

    return {
      videoPreview: video.video_preview,
      videoFile: video.video
    }
  }, [video])

  const imageUrls = useMemo(
    () => [
      intro_images?.image_1?.url,
      intro_images?.image_2?.url,
      intro_images?.image_3?.url
    ],
    [intro_images]
  )

  useHomeHeroImageLoader({
    imageUrls,
    enabled: isIntroImages
  })

  useEffect(() => {
    if (isIntroImages) return

    if (isIntroVideo && !intro_video?.url) {
      setLoaderState({ heroLoaded: true })
      return
    }

    if (
      isIntroNone &&
      !media?.poster?.url &&
      !media?.video?.url &&
      !media?.video_mobile?.url
    ) {
      setLoaderState({ heroLoaded: true })
    }
  }, [
    isIntroImages,
    isIntroNone,
    isIntroVideo,
    intro_video,
    media,
    setLoaderState
  ])

  const completeIntro = useCallback(() => {
    if (introCompletedRef.current) {
      return
    }

    introCompletedRef.current = true
    markIntroPlayed()
    lenis?.start()
    setHomeIntroRunning(false)
  }, [lenis, markIntroPlayed, setHomeIntroRunning])

  const completeVideoIntro = useCallback(() => {
    if (videoIntroHandledRef.current) {
      return
    }

    videoIntroHandledRef.current = true

    gsap.to('[data-motion-video-intro]', {
      autoAlpha: 0,
      duration: 0.35,
      ease: 'power2.out',
      onComplete: () => {
        setVideoIntroDismissed(true)
        completeIntro()
        skipTimelineRef.current?.play(0)
      }
    })
  }, [completeIntro])

  useEffect(() => {
    if (!isFirstVisit.current || !isIntroNone) return

    markIntroPlayed()
  }, [isIntroNone, markIntroPlayed])

  // Blocco scroll e header durante la prima intro
  useEffect(() => {
    if (!isFirstVisit.current) return
    if (!isIntroImages && !isIntroVideo) return

    lenis?.stop()
    setHomeIntroRunning(true)

    return () => {
      lenis?.start()
      setHomeIntroRunning(false)
    }
  }, [isIntroImages, isIntroVideo, lenis, setHomeIntroRunning])

  useGSAP(
    () => {
      if (!rootRef.current) return

      const introTimeScale = 0.7
      const introDuration = 5
      const titleDuration = 0.8
      const imageDuration = 0.3
      const getIntroPosition = (startPercentage: number) =>
        introDuration * (startPercentage / 100)

      const selector = gsap.utils.selector(rootRef.current)

      const intro = selector('[data-motion-intro]')[0]
      const image0 = selector('[data-motion-image="0"]')[0]
      const image1 = selector('[data-motion-image="1"]')[0]
      const image2 = selector('[data-motion-image="2"]')[0]
      const image3 = selector('[data-motion-image="3"]')[0]
      const title0 = selector('[data-motion-title="0"]')[0]
      const title1 = selector('[data-motion-title="1"]')[0]
      const title2 = selector('[data-motion-title="2"]')[0]

      const content = selector('[data-motion-content]')[0]
      const title = selector('[data-motion-title="4"]')[0]
      const textEl = selector('[data-motion-text]')[0]
      const scrollCta = selector('[data-motion-scroll]')[0]
      const magnetic = selector('[data-motion-magnetic]')[0]

      const isElement = (value: unknown): value is Element =>
        value instanceof Element
      const titleTargets = [title0, title1, title2].filter(isElement)
      const introImageTargets = [image0, image1, image2].filter(isElement)

      if (!siteLoaded) return

      let titleAnimation: SharedAnimation | undefined
      if (title instanceof HTMLElement) {
        titleAnimation = linesRevealAnimation(title, false)
      }

      let textAnimation: SharedAnimation | undefined
      if (textEl instanceof HTMLElement) {
        textAnimation = linesRevealAnimation(textEl, false)
      }

      const introTimeline = gsap.timeline({ paused: true })
      introTimeline.timeScale(introTimeScale)

      const canRunFullIntroTimeline =
        isIntroImages &&
        isElement(intro) &&
        isElement(image0) &&
        isElement(image1) &&
        isElement(image2) &&
        isElement(image3) &&
        isElement(title0) &&
        isElement(title1) &&
        isElement(title2)

      if (canRunFullIntroTimeline) {
        introTimeline
          .set([title0, title1, title2], {
            y: '100%'
          })
          .set(content, {
            autoAlpha: 0
          })
          .set(image0, {
            opacity: 1
          })

          // Scale Images
          .fromTo(
            intro,
            {
              scale: 0
            },
            {
              scale: 1,
              duration: introDuration,
              ease: 'power1.inOut'
            },
            0
          )

          // Titolo Matter
          .fromTo(
            title0,
            {
              y: '100%'
            },
            {
              y: '0%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(10)
          )

          // Titolo da Matter a Innovation
          .fromTo(
            title0,
            {
              y: '0%'
            },
            {
              y: '-100%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(30)
          )

          .fromTo(
            title1,
            {
              y: '100%'
            },
            {
              y: '0%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(30)
          )

          // Image 2
          .fromTo(
            image1,
            {
              opacity: 0
            },
            {
              opacity: 1,
              duration: imageDuration,
              ease: 'power2.out'
            },
            getIntroPosition(33)
          )

          .fromTo(
            title1,
            {
              y: '0%'
            },
            {
              y: '-100%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(55)
          )

          // Image 3
          .fromTo(
            image2,
            {
              opacity: 0
            },
            {
              opacity: 1,
              duration: imageDuration,
              ease: 'power2.out'
            },
            getIntroPosition(59)
          )

          // Titolo Transformation out
          .fromTo(
            title2,
            {
              y: '100%'
            },
            {
              y: '0%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(55)
          )

          .fromTo(
            title2,
            {
              y: '0%'
            },
            {
              y: '-100%',
              duration: titleDuration,
              ease: 'power2.out'
            },
            getIntroPosition(85)
          )

          // Image 4
          .fromTo(
            image3,
            {
              opacity: 0
            },
            {
              opacity: 1,
              duration: imageDuration,
              ease: 'power2.out'
            },
            getIntroPosition(89)
          )

          // Reveal content
          .set(
            content,
            {
              autoAlpha: 1
            },
            getIntroPosition(85)
          )

          .call(completeIntro, [], getIntroPosition(95))
      }

      if (shouldPlayFullIntro && titleAnimation?.timeline) {
        introTimeline.add(titleAnimation.timeline, getIntroPosition(87))
      }

      if (shouldPlayFullIntro && textAnimation?.timeline) {
        introTimeline.add(textAnimation.timeline, getIntroPosition(89))
      }

      if (isElement(scrollCta)) {
        introTimeline.to(
          scrollCta,
          {
            opacity: 1,
            duration: 1,
            ease: 'power2.out'
          },
          getIntroPosition(95)
        )
      }

      if (isElement(magnetic)) {
        introTimeline.to(
          magnetic,
          {
            opacity: 1,
            duration: 1,
            ease: 'power2.out'
          },
          getIntroPosition(95)
        )
      }

      const skipTimeline = gsap.timeline({ paused: true })

      if (isElement(intro)) {
        skipTimeline.set(intro, {
          scale: 1
        })
      }

      if (titleTargets.length > 0) {
        skipTimeline.set(titleTargets, {
          y: '-100%'
        })
      }

      if (introImageTargets.length > 0) {
        skipTimeline.set(introImageTargets, {
          opacity: 0
        })
      }

      if (isElement(image3)) {
        skipTimeline.set(image3, {
          opacity: 0
        })
      }

      skipTimeline.set(content, {
        autoAlpha: 1
      })

      if (isElement(image3)) {
        skipTimeline.fromTo(
          image3,
          {
            opacity: 0
          },
          {
            opacity: 1,
            duration: 0.8,
            ease: 'power2.out'
          },
          0.1
        )
      }

      if (isElement(scrollCta)) {
        skipTimeline.to(
          scrollCta,
          {
            opacity: 1,
            duration: 0.6,
            ease: 'power2.out'
          },
          0.55
        )
      }

      if (isElement(magnetic)) {
        skipTimeline.to(
          magnetic,
          {
            opacity: 1,
            duration: 0.6,
            ease: 'power2.out'
          },
          0.55
        )
      }

      if (!shouldPlayFullIntro && titleAnimation?.timeline) {
        skipTimeline.add(titleAnimation.timeline, 0.2)
      }

      if (!shouldPlayFullIntro && textAnimation?.timeline) {
        skipTimeline.add(textAnimation.timeline, 0.3)
      }

      skipTimelineRef.current = skipTimeline

      if (shouldPlayFullIntro && canRunFullIntroTimeline) {
        introTimeline.play()
      } else if (showVideoIntro) {
        skipTimeline.pause(0)
      } else {
        skipTimeline.play()
      }

      if (GSAP_DEBUG && typeof window !== 'undefined') {
        gsap.registerPlugin(GSDevTools)

        const debugWindow = window as HomeHeroDebugWindow
        const debugState = debugWindow.__MODULNOVA_GSAP__ || {}
        const existingDevTools = debugState.homeHeroDevTools

        safelyKillDevTools(existingDevTools)

        let homeHeroDevTools: HomeHeroDevTools | undefined

        try {
          homeHeroDevTools = GSDevTools.create({
            animation: introTimeline,
            id: 'home-hero-intro'
          })
        } catch (error) {
          console.warn('[HomeHero] GSDevTools init failed', error)
          return
        }

        debugWindow.__MODULNOVA_GSAP__ = {
          ...debugState,
          homeHeroIntro: introTimeline,
          homeHeroDevTools
        }

        return () => {
          const nextDebugState = debugWindow.__MODULNOVA_GSAP__

          if (!nextDebugState) {
            return
          }

          if (nextDebugState.homeHeroIntro === introTimeline) {
            delete nextDebugState.homeHeroIntro
          }

          if (nextDebugState.homeHeroDevTools === homeHeroDevTools) {
            safelyKillDevTools(homeHeroDevTools)
            delete nextDebugState.homeHeroDevTools
          }

          if (
            !nextDebugState.homeHeroIntro &&
            !nextDebugState.homeHeroDevTools
          ) {
            delete debugWindow.__MODULNOVA_GSAP__
          }
        }
      }
    },
    {
      scope: rootRef,
      dependencies: [completeIntro, shouldPlayFullIntro, siteLoaded]
    }
  )

  return (
    <>
      <Section
        className={cn(
          'relative z-0 -mt-[var(--header-height)] h-[calc(var(--vh)*100)] w-screen overflow-hidden text-text-heading-dark'
        )}
        ref={rootRef}
        data-layout="home-hero"
      >
        <div className="absolute inset-0 z-0 opacity-80" data-motion-intro>
          {isIntroImages
            ? [
                intro_images?.image_1,
                intro_images?.image_2,
                intro_images?.image_3
              ].map((image, i) => (
                <img
                  key={image?.url || i}
                  src={image?.url || ''}
                  alt="Modulnova"
                  className={cn(
                    'absolute z-10 block size-full object-cover opacity-0'
                  )}
                  style={{
                    zIndex: i
                  }}
                  data-motion-image={i}
                />
              ))
            : null}
          {media?.poster?.url || media?.video?.url ? (
            <Media
              media={media}
              className="absolute inset-0 z-0 h-full w-full object-cover opacity-0"
              unlockLoaderOnReady={isIntroNone}
              parentProps={{
                'data-motion-image': '3',
                style: {
                  zIndex: 3
                }
              }}
            />
          ) : null}
        </div>
        {isIntroImages ? (
          <div className="absolute inset-0 z-20">
            {[
              intro_images?.title_1,
              intro_images?.title_2,
              intro_images?.title_3
            ].map((title, i) => (
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden"
                key={i}
                style={{
                  zIndex: 20 + i
                }}
              >
                <div
                  data-motion-title={i}
                  className="text-h2 block translate-y-full leading-[1]"
                >
                  {title}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {showVideoIntro && introVideoMedia ? (
          <div className="absolute inset-0 z-[120]" data-motion-video-intro>
            <Media
              media={introVideoMedia}
              className="absolute inset-0 h-full w-full"
              fit="cover"
              autoPlay
              muted
              playsInline
              controls={false}
              loop={false}
              unlockLoaderOnReady
              onEnded={completeVideoIntro}
            />
            {showSkipIntro ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-[24px] z-10 flex justify-center md:bottom-[42px]">
                <button
                  type="button"
                  className="text-trim pointer-events-auto rounded-radius-minimal border border-[white]/40 bg-[black]/20 p-spacing-xs text-d-regular text-[white] backdrop-blur-sm transition hover:bg-[black]/35"
                  onClick={completeVideoIntro}
                >
                  {strings?.labels?.skip_intro || 'Skip Intro'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <Container
          className="absolute inset-0 z-50 h-full opacity-0"
          data-motion-content
        >
          <div className="relative flex h-full flex-col justify-center gap-spacing-md">
            {title ? (
              <h1
                data-motion-title="4"
                className="text-h2 text-trim block leading-[1]"
              >
                {parse(title.replace(/\r\n/g, '<br />'))}
              </h1>
            ) : null}
            <div
              className="text-regular block w-[300px] text-text-primary-dark"
              data-motion-text
            >
              {text}
            </div>
            <ScrollCta
              data-motion-scroll
              className="absolute bottom-[20px] left-0 z-10 opacity-0 md:bottom-d-spacer-lg"
            />
          </div>
        </Container>

        {heroMagneticVideo.videoPreview?.url ? (
          <MagneticVideo
            data-motion-magnetic
            className="absolute bottom-[80px] right-padding-lg z-[200] hidden size-[180px] opacity-0 md:block"
            video={heroMagneticVideo.videoFile}
            video_preview={heroMagneticVideo.videoPreview}
          />
        ) : null}
      </Section>
      <Spacer variant="xxl" />
    </>
  )
}

export default HomeHero
